import React, { useEffect, useRef, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import AddressManager from '../../app/managers/addressManager'
import { Localize } from '../../app/lang/langs'

/**
 * Address input backed by the French BAN address API.
 * The value can only be set by picking one of the suggestions — free text
 * typed by the user reverts to the last selected value on blur, so stored
 * addresses are always real, correctly spelled addresses.
 *
 * Props:
 * - value: string — current stored value (label or city name)
 * - type: optional BAN type filter, e.g. "municipality" for city-only search
 * - placeholder: optional lang key for the input placeholder
 * - style: optional style forwarded to the root element
 * - onSelect(option|null): called with the picked suggestion
 *   ({ id, label, name, postcode, city }) or null when cleared
 */
export function AddressAutocomplete(props) {

    const { value, type, onSelect } = props

    const [options, setOptions] = useState([])
    const [inputValue, setInputValue] = useState(value || "")
    const [loading, setLoading] = useState(false)
    const debounceRef = useRef(null)

    // keep the displayed text in sync when the parent loads/saves data
    useEffect(() => {
        setInputValue(value || "")
    }, [value])

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [])

    function getDisplayLabel(option) {
        if (!option) return ""
        return type === "municipality" ? option.city : option.label
    }

    function handleInputChange(event, newInput, reason) {
        if (reason === "input") {
            setInputValue(newInput)
            if (debounceRef.current) clearTimeout(debounceRef.current)
            debounceRef.current = setTimeout(async () => {
                setLoading(true)
                const results = await AddressManager.searchAddresses(newInput, type)
                setOptions(results)
                setLoading(false)
            }, 300)
        } else if (reason === "clear") {
            setInputValue("")
            setOptions([])
            if (onSelect) onSelect(null)
        }
    }

    function handleChange(event, option) {
        if (option) {
            setInputValue(getDisplayLabel(option))
            if (onSelect) onSelect(option)
        }
    }

    function handleBlur() {
        // typed text that was not picked from the suggestions is discarded:
        // revert to the last selected value, or clear if the field was emptied
        if (!inputValue && value) {
            if (onSelect) onSelect(null)
        } else {
            setInputValue(value || "")
        }
        setOptions([])
    }

    return (
        <Autocomplete
            options={options}
            value={null}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onChange={handleChange}
            onBlur={handleBlur}
            loading={loading}
            filterOptions={(x) => x}
            getOptionLabel={(option) =>
                type === "municipality"
                    ? `${option.city} (${option.postcode})`
                    : option.label
            }
            isOptionEqualToValue={(option, val) => option.id === val?.id}
            noOptionsText={Localize("ADDRESS_NO_RESULTS")}
            loadingText={Localize("ADDRESS_LOADING")}
            size="small"
            style={props.style}
            renderOption={(optionProps, option) => (
                <li {...optionProps} key={option.id}>
                    {type === "municipality"
                        ? `${option.city} (${option.postcode})`
                        : option.label}
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={Localize(props.placeholder || "ADDRESS_SEARCH_PLACEHOLDER")}
                    variant="outlined"
                    size="small"
                />
            )}
        />
    )
}
